'use client';

/**
 * @brisa/ui · components/ui/select — shadcn Select (Facelift v1)
 *
 * Feature: migracao-facelift-v1 · ADR §D6 · adicionado Bruno 2026-05-16
 *
 * Wrapper Radix UI react-select com estilo facelift. É o "DropDown filter"
 * do FilterBar na ClubesPage (ex: "Bairro: Gràcia ▼") — Figma node 194:4927
 * (variant Default, fechado) dentro do frame /clubes node 194:3943.
 *
 * Trigger specs (Figma 194:4927):
 *   - Layout horizontal · SPACE_BETWEEN · CENTER
 *   - Padding: 16px V × 24px H · itemSpacing 8px
 *   - Bg: white · cornerRadius 24px (rounded-3xl) · height 48px
 *   - Label "Bairro:" → Archivo Regular 14px · text-40 (opacity 0.4)
 *   - Value → Archivo Regular 14px · text-100 (opacity 1)
 *   - Ícone trailing: ChevronDown 16×16 stroke 1.5 · rotaciona 180° em open
 *
 * Content (popover) — herda convenção do dropdown-menu.tsx:
 *   - bg-white · rounded-[16px] · shadow-2xl · border text-08
 *   - padding 8px (p-2) · animação Radix enter/exit + zoom-95
 *
 * Items:
 *   - hover/focus bg-text-08 · ícone Check trailing quando selecionado
 *   - text-100 · Archivo Regular 14px (text-sm)
 *
 * Pattern: pareia com dropdown-menu.tsx mas semanticamente é "seleção de
 * valor único" (não menu de ação). Não inclui label "Bairro:" hardcoded —
 * consumer passa via children do SelectValue ou via composição própria.
 *
 * 'use client' obrigatório — Radix Select orquestra portal/state/positioning.
 */
import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';

import { cn } from '../../lib/utils';

// ---------------------------------------------------------------------------
// Root primitives — re-exportados direto (sem styling)
// ---------------------------------------------------------------------------

export const Select = SelectPrimitive.Root;
export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = SelectPrimitive.Value;

// ---------------------------------------------------------------------------
// Trigger — botão visível: pill branca arredondada com chevron trailing
// ---------------------------------------------------------------------------

export const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      // Pill base (Figma 194:4927): h-12 (48px) · rounded-3xl (24px) · bg-white
      // · padding 16/24 · gap-2 (8px) · text-sm Archivo · text-100
      'group flex h-12 items-center justify-between gap-2 rounded-3xl bg-white px-6 py-4',
      'text-sm font-normal text-[hsl(var(--text-100))]',
      // Placeholder herda text-40 — Radix aplica data-placeholder no Value
      '[&>span[data-placeholder]]:text-[hsl(var(--text-40))]',
      // Transição cor/sombra suave em hover/focus
      'transition-colors duration-base ease-out',
      // a11y — focus ring amarelo + disabled
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      className,
    )}
    {...props}
  >
    {children}
    {/* ChevronDown 16px stroke 1.5 — rotaciona 180° em data-state=open */}
    <SelectPrimitive.Icon asChild>
      <ChevronDown
        size={16}
        aria-hidden="true"
        strokeWidth={1.5}
        className={cn(
          'shrink-0 text-[hsl(var(--text-100))]',
          'transition-transform duration-200 ease-out',
          'group-data-[state=open]:rotate-180',
        )}
      />
    </SelectPrimitive.Icon>
  </SelectPrimitive.Trigger>
));
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

// ---------------------------------------------------------------------------
// Scroll buttons (aparecem só quando a lista excede a viewport)
// ---------------------------------------------------------------------------

export const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronUp size={16} aria-hidden="true" strokeWidth={1.5} />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

export const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn('flex cursor-default items-center justify-center py-1', className)}
    {...props}
  >
    <ChevronDown size={16} aria-hidden="true" strokeWidth={1.5} />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName = SelectPrimitive.ScrollDownButton.displayName;

// ---------------------------------------------------------------------------
// Content — popover branco com border-radius 16, shadow-2xl, animação enter/exit
// ---------------------------------------------------------------------------

export const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = 'popper', sideOffset = 8, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      position={position}
      sideOffset={sideOffset}
      className={cn(
        // Container — mesma convenção visual de dropdown-menu.tsx
        'relative z-50 max-h-[--radix-select-content-available-height] min-w-[8rem] overflow-hidden',
        'rounded-[16px] border border-[hsl(var(--text-08))] bg-white text-[hsl(var(--text-100))] shadow-2xl',
        // Animação Radix data-state
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        // Slide-in conforme lado de abertura
        'data-[side=bottom]:slide-in-from-top-2 data-[side=top]:slide-in-from-bottom-2',
        'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
        // Quando position=popper, espelha width do trigger
        position === 'popper' && 'w-[--radix-select-trigger-width]',
        className,
      )}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          'p-2',
          position === 'popper' &&
            'h-[--radix-select-trigger-height] w-full min-w-[--radix-select-trigger-width]',
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

// ---------------------------------------------------------------------------
// Label (opcional — para agrupar opções com cabeçalho)
// ---------------------------------------------------------------------------

export const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn(
      'px-3 py-2 text-xs font-semibold uppercase tracking-wider text-[hsl(var(--text-48))]',
      className,
    )}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

// ---------------------------------------------------------------------------
// Item — opção selecionável; Check trailing quando selecionado
// ---------------------------------------------------------------------------

export const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={cn(
      // Linha base — flex space-between, padding 12/8, text-sm
      'relative flex w-full cursor-pointer select-none items-center justify-between gap-2',
      'rounded-md px-3 py-2 text-sm font-normal text-[hsl(var(--text-100))]',
      'outline-none transition-colors',
      // Hover/focus — bg text-08 (mesmo do dropdown-menu)
      'focus:bg-[hsl(var(--text-08))] hover:bg-[hsl(var(--text-08))]',
      // Disabled
      'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
      className,
    )}
    {...props}
  >
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    <SelectPrimitive.ItemIndicator asChild>
      <Check
        size={16}
        aria-hidden="true"
        strokeWidth={1.5}
        className="shrink-0 text-[hsl(var(--text-100))]"
      />
    </SelectPrimitive.ItemIndicator>
  </SelectPrimitive.Item>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

export const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 my-1 h-px bg-[hsl(var(--text-08))]', className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;
